import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { HelpPageContext } from '@/types/help';

/**
 * State interface for the help panel store with methods for managing help UI.
 */
export interface HelpPanelState {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  panelWidth: number;
  toggle: () => void;
  searchQuery: string;

  recentTopics: string[];
  clearSearch: () => void;
  selectedTopicId: string | null;
  currentContext: HelpPageContext;
  setPanelWidth: (width: number) => void;
  setSearchQuery: (query: string) => void;
  selectTopic: (topicId: string | null) => void;
  setContext: (context: HelpPageContext) => void;
}

export const useHelpStore = create<HelpPanelState>()(
  persist(
    (set, get) => ({
      isOpen: false,
      panelWidth: 400,
      searchQuery: '',
      recentTopics: [],
      selectedTopicId: null,
      currentContext: 'global',

      open: () =>
        set({
          isOpen: true,
        }),
      clearSearch: () =>
        set({
          searchQuery: '',
        }),
      setSearchQuery: (query) =>
        set({
          searchQuery: query,
        }),

      setContext: (context) =>
        set({
          currentContext: context,
        }),

      toggle: () =>
        set((state) => ({
          isOpen: !state.isOpen,
        })),
      close: () =>
        set({
          isOpen: false,
          selectedTopicId: null,
        }),
      setPanelWidth: (width) =>
        set({
          panelWidth: Math.max(320, Math.min(600, width)),
        }),
      selectTopic: (topicId) => {
        if (!topicId) {
          set({
            selectedTopicId: null,
          });

          return;
        }

        const { recentTopics } = get();
        const updated = [topicId, ...recentTopics.filter((id) => id !== topicId)].slice(0, 5);
        set({
          recentTopics: updated,
          selectedTopicId: topicId,
        });
      },
    }),
    {
      name: 'nexus-admin-help',
      partialize: (state) => ({
        panelWidth: state.panelWidth,
        recentTopics: state.recentTopics,
      }),
    },
  ),
);
